import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRightLeft, ClipboardCheck, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLocations } from "@/context/LocationContext";
import { useScanner } from "@/context/ScannerContext";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui-bits";
import StockTakeScanBar from "@/components/stocktake/StockTakeScanBar";
import StockTakeItemDialog from "@/components/stocktake/StockTakeItemDialog";
import StockTakeItemsList from "@/components/stocktake/StockTakeItemsList";
import StockTakeComparison from "@/components/stocktake/StockTakeComparison";
import StockTakeApplyDialog from "@/components/stocktake/StockTakeApplyDialog";
import StockTakeLabels from "@/components/stocktake/StockTakeLabels";
import StockTakeHistory from "@/components/stocktake/StockTakeHistory";

function StartPanel({ onStart }) {
  return (
    <div className="panel p-8 sm:p-10 max-w-2xl" data-testid="stocktake-start-panel">
      <ClipboardCheck className="h-10 w-10 text-primary mb-4" />
      <h2 className="font-display text-lg font-bold">Re-count all raw-material stock</h2>
      <p className="text-sm text-stone-600 dark:text-stone-300 mt-2 leading-relaxed">
        Scan every bag, tub and pallet you physically have. Everything goes into a draft first, so live stock
        and production are not affected. When you have finished, switch over with one click: old stock goes to 0
        (kept in history) and every counted item becomes a new intake with a new INT- batch code.
      </p>
      <Button className="h-12 px-6 mt-6" onClick={onStart} data-testid="stocktake-start">Start stock take</Button>
    </div>
  );
}

export default function StockTake() {
  const { user } = useAuth();
  const { selected } = useLocations();
  const scanner = useScanner();
  const [session, setSession] = useState(undefined);
  const [lists, setLists] = useState({ materials: [], suppliers: [], locations: [] });
  const [history, setHistory] = useState([]);
  const [dialog, setDialog] = useState({ open: false, scan: null });
  const [applyOpen, setApplyOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    const [c, h] = await Promise.all([api.get("/stock-take/current"), api.get("/stock-take/sessions")]);
    setSession(c.data || null);
    setHistory(h.data);
  }, []);

  useEffect(() => {
    if (user?.role !== "admin") return;
    load();
    Promise.all([api.get("/materials"), api.get("/suppliers"), api.get("/locations")]).then(([m, s, l]) =>
      setLists({ materials: m.data, suppliers: s.data, locations: l.data })
    );
  }, [user, load]);

  const handleScan = useCallback(async (code) => {
    try {
      const { data } = await api.get(`/stock-take/resolve/${encodeURIComponent(code)}`);
      setDialog({ open: true, scan: data });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Scan failed");
    }
  }, []);

  useEffect(() => {
    if (!session || dialog.open) return undefined;
    return scanner.subscribe((code) => handleScan(code));
  }, [scanner, session, dialog.open, handleScan]);

  if (user?.role !== "admin") {
    return <div className="text-sm text-stone-500" data-testid="stocktake-admin-only">Stock take is available to admins only.</div>;
  }

  const start = async () => {
    await api.post("/stock-take/sessions");
    load();
  };
  const removeItem = async (it) => {
    if (!window.confirm(`Remove ${it.material_name} (${it.supplier_batch}) from the draft?`)) return;
    await api.delete(`/stock-take/sessions/${session.id}/items/${it.id}`);
    load();
  };
  const discard = async () => {
    if (!window.confirm("Discard this draft? Counted items will be thrown away. Live stock is not changed.")) return;
    await api.post(`/stock-take/sessions/${session.id}/discard`);
    toast.success("Draft discarded");
    load();
  };
  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/stock-take/sessions/${session.id}/apply`);
      setApplyOpen(false);
      setResult(data);
      toast.success(`Switched: ${data.created} new intakes live`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Switch failed");
    } finally {
      setBusy(false);
    }
  };
  const reprint = async (s) => {
    const { data } = await api.get(`/stock-take/sessions/${s.id}/intakes`);
    setResult({ wiped: s.applied_rows_wiped, intakes: data });
  };

  const items = session?.items || [];
  const closedHistory = history.filter((s) => s.status !== "open");

  return (
    <div>
      <PageHeader title="Stock Take" eyebrow="Inbound · Re-count stock">
        {session && !result && (
          <>
            <Button variant="outline" className="h-12 gap-2 text-rose-600" onClick={discard} data-testid="stocktake-discard">
              <XCircle className="h-4 w-4" /> Discard draft
            </Button>
            <Button className="h-12 px-5 gap-2" onClick={() => setApplyOpen(true)} disabled={!items.length} data-testid="stocktake-switch">
              <ArrowRightLeft className="h-5 w-5" /> Switch to new stock ({items.length})
            </Button>
          </>
        )}
      </PageHeader>

      {session === undefined && <div className="text-sm text-stone-500">Loading…</div>}
      {result && <StockTakeLabels result={result} onDone={() => setResult(null)} />}
      {!result && session === null && <StartPanel onStart={start} />}
      {!result && session && (
        <div className="space-y-6">
          <div className="text-xs text-stone-500" data-testid="stocktake-session-info">
            Draft started {session.created_at?.slice(0, 16).replace("T", " ")} by {session.created_by_name}. Live stock is unchanged until you switch.
          </div>
          <StockTakeScanBar onScan={handleScan} onManual={() => setDialog({ open: true, scan: null })} paused={dialog.open || applyOpen} />
          <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
            <div className="xl:col-span-3"><StockTakeItemsList items={items} onRemove={removeItem} /></div>
            <div className="xl:col-span-2"><StockTakeComparison rows={session.comparison || []} /></div>
          </div>
        </div>
      )}

      {!result && <div className="mt-6"><StockTakeHistory sessions={closedHistory} onReprint={reprint} /></div>}

      {session && (
        <StockTakeItemDialog
          open={dialog.open}
          scan={dialog.scan}
          onClose={() => setDialog({ open: false, scan: null })}
          onSaved={() => { setDialog({ open: false, scan: null }); load(); }}
          sessionId={session.id}
          items={items}
          defaultLocation={selected}
          {...lists}
        />
      )}
      <StockTakeApplyDialog open={applyOpen} onClose={() => setApplyOpen(false)} onConfirm={apply} busy={busy} items={items} comparison={session?.comparison || []} />
    </div>
  );
}
